import {
  useCallback,
  useEffect,
} from 'react';

import { useSelector } from 'react-redux';

import { Auth } from '../../constants';
import { authSlice } from '../../slices/auth';
import { usePutLinkAccountAuthenticationMutation } from '../../slices/auth/putLinkAccountsAuthApi';

import {
  usePostAuthenticationPassword,
  UsePostAuthenticationPasswordParams,
} from './usePostAuthenticationPassword';

type UsePostAuthenticationPasswordAndUpgrade = UsePostAuthenticationPasswordParams;
type UsePostAuthenticationPasswordAndUpgradeProps = {
  userRoleType?: string;
};
// Hook composition = We can't do async/await due to the nature of 'epics' (observables)
export const usePostAuthenticationPasswordAndUpgrade = ({
  userRoleType,
}: UsePostAuthenticationPasswordAndUpgradeProps) => {
  const {
    execute: executePostAuthenticationPassword,
    isSuccess: isPostAuthenticationPasswordSuccess,
    error: postAuthenticationPasswordError,
    errorMessage: postAuthenticationPasswordErrorMessage,
    reset: resetPostAuthenticationPassword,
    isLoading: isPostAuthenticationPasswordLoading,
  } = usePostAuthenticationPassword();

  const [
    executePutLinkAccountAuthentication,
    {
      isLoading: putLinkAccountAuthenticationMutationIsLoading,
      isError: putLinkAccountAuthenticationMutationIsError,
      error: putLinkAccountAuthenticationMutationErrorMessage,
      isSuccess: putLinkAccountAuthenticationMutationSuccess,
    },
  ] = usePutLinkAccountAuthenticationMutation();

  const socialAuthProviderToLink = useSelector(authSlice.selectors.getSocialAuthProviderToLink);
  const facebookAccessToken = useSelector(authSlice.selectors.getFacebookAccessToken);
  const googleIdToken = useSelector(authSlice.selectors.getGoogleIdToken);
  const appleIdToken = useSelector(authSlice.selectors.getAppleIdToken);

  const execute = (requestData: UsePostAuthenticationPasswordAndUpgrade) => {
    executePostAuthenticationPassword(requestData);
  };

  const reset = () => {
    resetPostAuthenticationPassword();
  };

  useEffect(
    () => {
      if (userRoleType !== Auth.RoleType.Owner) {
        return;
      }

      if (!isPostAuthenticationPasswordSuccess || !socialAuthProviderToLink) {
        return;
      }

      executePutLinkAccountAuthentication(
        {
          socialAuthProviderToLink,
          facebookAccessToken,
          googleIdToken,
          appleIdToken,
        },
      );
    },
    [
      isPostAuthenticationPasswordSuccess,
      executePutLinkAccountAuthentication,
      socialAuthProviderToLink,
      googleIdToken,
      facebookAccessToken,
      appleIdToken,
      userRoleType,
    ],
  );

  return {
    error: postAuthenticationPasswordError
      || putLinkAccountAuthenticationMutationIsError,
    errorMessage:
      postAuthenticationPasswordErrorMessage
      || putLinkAccountAuthenticationMutationErrorMessage,
    isLoading:
      isPostAuthenticationPasswordLoading
      || putLinkAccountAuthenticationMutationIsLoading,
    isSuccess: putLinkAccountAuthenticationMutationSuccess || isPostAuthenticationPasswordSuccess,
    isLinkedAccountsSuccess: putLinkAccountAuthenticationMutationSuccess,
    execute,
    reset: useCallback(reset, [resetPostAuthenticationPassword]),
  };
};
